import { motion } from "framer-motion";
import { Flame, Star, Trophy } from "lucide-react";
import type { CompletionResult } from "@/core/data/db";

interface CompletionCardProps {
  result: CompletionResult;
  onNext: () => void;
  nextLabel?: string;
}

/** Shared "you completed it" summary with XP/streak/level deltas. */
export default function CompletionCard({
  result,
  onNext,
  nextLabel = "Next",
}: CompletionCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-primary/40 bg-primary/5 p-7"
    >
      <div className="flex items-center gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-xl bg-primary text-primary-fg shadow-glow">
          <Trophy className="h-6 w-6" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-text">Nice work!</h3>
          <p className="text-sm text-muted">Saved to your history.</p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3">
        <Stat icon={Star} label="XP gained" value={`+${result.xpGained}`} />
        <Stat icon={Flame} label="Streak" value={`${result.progress.streakDays}d`} highlight={result.streakIncreased} />
        <Stat icon={Trophy} label="Level" value={`${result.progress.level}`} highlight={result.leveledUp} />
      </div>

      {result.leveledUp && (
        <div className="mt-4 rounded-lg bg-primary/10 px-4 py-2 text-center text-sm font-medium text-primary">
          🎉 Level up! You reached level {result.progress.level}.
        </div>
      )}

      <button
        onClick={onNext}
        className="mt-5 w-full rounded-lg bg-primary px-4 py-2.5 font-semibold text-primary-fg transition-transform hover:scale-[1.02]"
      >
        {nextLabel}
      </button>
    </motion.div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  highlight = false,
}: {
  icon: typeof Star;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={[
        "rounded-xl border p-3 text-center",
        highlight ? "border-primary/50 bg-primary/10" : "border-border bg-surface",
      ].join(" ")}
    >
      <Icon className={`mx-auto h-5 w-5 ${highlight ? "text-primary" : "text-muted"}`} />
      <div className="mt-1 font-display text-lg font-bold text-text">{value}</div>
      <div className="text-[11px] text-muted">{label}</div>
    </div>
  );
}
